import { isPositiveParse } from '@app/utils/filter/parser/parse-result';
import { parse } from '@app/utils/filter/parser/parser';
import { ParserError } from '@app/utils/filter/parser/parser-error';
import { MapLike } from '@app/utils/filter/terms/map-like';
import { matchesTerm } from '@app/utils/filter/terms/matches-term';
import { isNotBlank } from '@app/utils/string';

type EntryWithMetaData = {
  meta?: MapLike<string, any[]>;
};

/**
 * Filters the given list of entries by the given query.
 * The query is split into terms by ' ' and all entries are matched against all terms.
 * To decide which entries are included in the result the reduce function is called for each term and the previous result.
 *
 * @param entries
 * @param query
 * @param reduce
 */
function filter<T extends EntryWithMetaData>(
  entries: T[],
  query: string,
  reduce: (a: boolean, b: boolean) => boolean
): { error?: ParserError; values: T[] } {
  let error: ParserError | undefined = undefined;
  let filteredEntries: T[] = [];

  if (isNotBlank(query)) {
    const parsedQuery = parse(query);

    if (isPositiveParse(parsedQuery)) {
      for (const entry of entries) {
        let matched: boolean = true;

        for (const term of parsedQuery.terms) {
          const entryMatches = matchesTerm(term, entry.meta);

          matched = reduce(matched, entryMatches);

          if (matched === false) {
            break;
          }
        }

        if (matched === true) {
          filteredEntries.push(entry);
        }
      }
    } else {
      error = parsedQuery.error;
      filteredEntries.push(...entries);
    }
  } else {
    filteredEntries.push(...entries);
  }

  return {
    error: error,
    values: filteredEntries,
  };
}

/**
 * Filters the given list of entries by the given query.
 * The query is split into terms by ' ' and an entry is included in the result if it matches all terms.
 *
 * @param entries
 * @param query
 */
export function filterExclusive<T extends EntryWithMetaData>(
  entries: T[],
  query: string
): { error?: ParserError; values: T[] } {
  return filter(entries, query, (a, b) => a && b);
}

/**
 * Filters the given list of entries by the given query.
 * The query is split into terms by ' ' and an entry is included in the result if it matches at least one term.
 *
 * @param entries
 * @param query
 */
export function filterInclusive<T extends EntryWithMetaData>(
  entries: T[],
  query: string
): { error?: ParserError; values: T[] } {
  return filter(entries, query, (a, b) => a || b);
}
